import { useEffect, useState } from "react";
import { Clock, Hash, ArrowRight } from "lucide-react";
import { PaymentCategory } from "@/features/applications/repositories/licences-repository.ts";
import Button from "@/components/buttons/Button";
import {
    normalizeTzPhone,
    isValidTzPhone,
    type MobileProvider,
    type ZanCustomerFields,
} from "@/features/applications/services/applications-services";

/**
 * What the applicant chose on the payment step, handed back to the wizard so it can file the
 * application and — when they are paying now — raise the bill straight afterwards.
 */
export type SubmitPaymentChoice = {
    /** Filing without paying: no bill is raised, the application is saved as a draft. */
    payLater: boolean;
    /** Who to bill and on which line. Only set when paying now. */
    phoneNumber?: string;
    provider?: MobileProvider;
};

interface IProps extends ZanCustomerFields {
    onSubmit?: (choice: SubmitPaymentChoice) => void;
    paymentCategory: PaymentCategory;
    totalFees?: string;
    defaultPhoneNumber?: string;
    /** True while the application is being filed and its control number requested. */
    isSubmitting?: boolean;
}

type Mode = 'choose' | 'collect';

const PROVIDERS: { value: MobileProvider; label: string }[] = [
    { value: 'MPESA', label: 'M-Pesa (Vodacom)' },
    // Tigo Pesa rebranded to Mixx by Yas; one wallet, one name.
    { value: 'TIGOPESA', label: 'Mixx by Yas' },
    { value: 'AIRTEL_MONEY', label: 'Airtel Money' },
    { value: 'HALOPESA', label: 'Halo Pesa' },
    { value: 'EAZYPESA', label: 'Eazy Pesa (Zantel)' },
    { value: 'TPESA', label: 'T-Pesa (TTCL)' },
];

function detectProvider(phone: string): MobileProvider | '' {
    const digits = phone.replace(/\D/g, '');
    const suffix = digits.length === 12 ? digits.slice(3) : digits.length === 10 ? digits.slice(1) : null;
    if (!suffix) return '';
    const p3 = suffix.slice(0, 3);
    if (['074', '075', '076', '077', '078'].includes(p3)) return 'MPESA';
    if (['065', '067', '071'].includes(p3)) return 'TIGOPESA';
    if (['068', '069'].includes(p3)) return 'AIRTEL_MONEY';
    if (p3 === '062') return 'HALOPESA';
    if (p3 === '079') return 'EAZYPESA';
    if (p3 === '041') return 'TPESA';
    return '';
}

/**
 * The payment step of an application wizard: pay now, or file now and pay later.
 *
 * It collects a mobile number and provider and nothing else. It does NOT raise the bill —
 * a control number is stored against the application's `applicationId`, which does not exist
 * until the application has been filed, so generating one here produced a bill orphaned from
 * its application. The wizard submits first and requests the control number with the id the
 * API hands back.
 */
export default function ControlNumberPaymentForm({
    onSubmit,
    paymentCategory: _paymentCategory,
    totalFees: _totalFees,
    defaultPhoneNumber,
    isSubmitting,
}: IProps) {
    const [mode, setMode] = useState<Mode>('choose');
    const [phone, setPhone] = useState('');
    const [provider, setProvider] = useState<MobileProvider | ''>('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!defaultPhoneNumber) return;
        const normalized = normalizeTzPhone(defaultPhoneNumber);
        if (!normalized) return;
        const local = normalized.startsWith('255') ? normalized.slice(3) : normalized;
        setPhone(local);
        const detected = detectProvider(normalized);
        if (detected) setProvider(detected);
    }, [defaultPhoneNumber]);

    const handlePhoneChange = (val: string) => {
        const d = val.replace(/\D/g, '');
        setPhone(d);
        const detected = detectProvider(d);
        if (detected) setProvider(detected);
    };

    const handlePayNow = () => {
        const normalized = normalizeTzPhone(phone);
        if (!isValidTzPhone(normalized)) {
            setError('Phone number must be 12 digits starting with country code, e.g. 255712345678.');
            return;
        }
        if (!provider) {
            setError('Please select your mobile provider.');
            return;
        }
        setError(null);
        onSubmit?.({ payLater: false, phoneNumber: normalized, provider: provider as MobileProvider });
    };

    // — Choice screen —
    if (mode === 'choose') {
        return (
            <div className="space-y-3">
                <button
                    type="button"
                    onClick={() => setMode('collect')}
                    className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 border-orange-200 bg-orange-50 hover:border-orange-400 transition-all text-left"
                >
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Hash className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 text-sm">Pay Now</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Your control number is issued as soon as the application is submitted.
                        </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-orange-400 ml-auto self-center flex-shrink-0" />
                </button>

                <button
                    type="button"
                    onClick={() => onSubmit?.({ payLater: true })}
                    className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-gray-300 bg-white transition-all text-left"
                >
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Clock className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 text-sm">Pay Later</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Save the application as a draft. No control number is issued until you pay.
                        </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300 ml-auto self-center flex-shrink-0" />
                </button>
            </div>
        );
    }

    // — Collect phone + provider, then submit —
    return (
        <div className="space-y-4">
            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Phone Number
                </label>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+255</span>
                    <input
                        type="tel"
                        value={phone}
                        onChange={e => handlePhoneChange(e.target.value)}
                        placeholder="7XXXXXXXX"
                        maxLength={12}
                        className="input input-default pl-14 w-full"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Network Provider
                </label>
                <div className="grid grid-cols-3 gap-2">
                    {PROVIDERS.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            onClick={() => setProvider(p.value)}
                            className={`px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
                                provider === p.value
                                    ? 'border-orange-400 bg-orange-50 text-orange-700'
                                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-600">{error}</div>
            )}

            <Button
                intent="primary"
                size="md"
                className="w-full"
                loading={isSubmitting}
                disabled={!phone || !provider || isSubmitting}
                onClick={handlePayNow}
                rightIcon={<ArrowRight className="w-4 h-4" />}
            >
                Submit Application
            </Button>
        </div>
    );
}
